import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  TemplateRef,
  TrackByFunction,
  ViewChild,
} from '@angular/core';

import {
  VirtualTableHeaderTemplateDirective,
  VirtualTableRowTemplateDirective,
} from './virtual-scroll-table.directive';

@Component({
  selector: 'lib-virtual-scroll-table',
  templateUrl: './virtual-scroll-table.component.html',
  styleUrls: ['./virtual-scroll-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class VirtualScrollTableComponent<TItem extends object> implements AfterViewInit, OnChanges, OnDestroy {
  @Input() rowType: 'collapse' | 'separate' = 'separate';
  @Input() withAction = true;
  @Input() data: TItem[] = [];
  @Input() itemSize = 70;
  @Input() trackBy: TrackByFunction<TItem>;
  @Input() headerClass = '';
  // The table scrolls sideways inside the component instead of the cells being squeezed past
  // readability and, below the stacking breakpoint, every row folding into a card.
  @Input() horizontalScroll = false;
  // Any CSS length: the width the columns need to stay readable, below which the body scrolls
  // sideways instead of squeezing them further. Above it nothing changes - the columns share the
  // extra space the way an auto-layout table always has, so a wide screen keeps dynamic cells.
  @Input() minTableWidth?: string;

  /** How many items before the end to trigger scrolledNearEnd */
  @Input() scrollEndThreshold = 5;

  @Output() scrolledNearEnd = new EventEmitter<void>();

  @ContentChild(VirtualTableHeaderTemplateDirective, { read: TemplateRef })
  headers?: TemplateRef<any>;

  @ContentChild(VirtualTableRowTemplateDirective, { read: TemplateRef })
  rows?: TemplateRef<any>;

  @ViewChild(CdkVirtualScrollViewport) viewport: CdkVirtualScrollViewport;
  @ViewChild('headerTable', { static: false }) headerTable: ElementRef<HTMLTableElement>;
  @ViewChild('bodyTable', { static: false }) bodyTable: ElementRef<HTMLTableElement>;
  @ViewChild('headerScroll', { static: false }) headerScroll: ElementRef<HTMLElement>;

  // Drives the scroll-mode rules in the stylesheet.
  @HostBinding('class.horizontal-scroll') get isHorizontalScroll(): boolean {
    return this.horizontalScroll;
  }

  private lastEmittedDataLength = 0;
  private resizeObserver: ResizeObserver | null = null;
  private detachHeaderScrollSync: (() => void) | null = null;

  constructor(private ngZone: NgZone) {}

  ngAfterViewInit(): void {
    setTimeout(() => this.viewport?.checkViewportSize(), 0);
    this.setupColumnWidthSync();
    if (this.horizontalScroll) {
      this.setupHeaderScrollSync();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] && this.viewport) {
      if (this.data.length < this.lastEmittedDataLength) {
        this.lastEmittedDataLength = 0;
      }
    }
    if (changes['data']) {
      // Sync columns after data changes and DOM updates
      setTimeout(() => this.syncColumnWidths(), 0);
    }
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    this.detachHeaderScrollSync?.();
    this.detachHeaderScrollSync = null;
  }

  onScrollIndexChange(): void {
    if (!this.viewport) {
      return;
    }
    const end = this.viewport.getRenderedRange().end;
    const total = this.viewport.getDataLength();

    if (total > 0 && end >= total - this.scrollEndThreshold && total > this.lastEmittedDataLength) {
      this.lastEmittedDataLength = total;
      this.scrolledNearEnd.emit();
    }
  }

  defaultTrackBy(index: number): number {
    return index;
  }

  private setupColumnWidthSync(): void {
    this.ngZone.runOutsideAngular(() => {
      this.resizeObserver = new ResizeObserver(() => {
        this.syncColumnWidths();
      });

      // Observe the viewport content wrapper so we detect when rows render/change
      const contentWrapper = this.viewport?.elementRef?.nativeElement;
      if (contentWrapper) {
        this.resizeObserver.observe(contentWrapper);
      }
    });
  }

  // The body is the only element that scrolls sideways - its bars stay in view, which is the point of
  // the mode. The header is clipped instead and dragged along from here, so the columns stay aligned.
  private setupHeaderScrollSync(): void {
    const viewportEl = this.viewport?.elementRef?.nativeElement;
    const headerEl = this.headerScroll?.nativeElement;
    if (!viewportEl || !headerEl) {
      return;
    }

    this.ngZone.runOutsideAngular(() => {
      const onScroll = () => {
        headerEl.scrollLeft = viewportEl.scrollLeft;
      };
      viewportEl.addEventListener('scroll', onScroll, { passive: true });
      this.detachHeaderScrollSync = () => viewportEl.removeEventListener('scroll', onScroll);
    });
  }

  private syncColumnWidths(): void {
    if (!this.bodyTable?.nativeElement || !this.headerTable?.nativeElement) {
      return;
    }

    const bodyRow = this.bodyTable.nativeElement.querySelector('tbody tr');
    if (!bodyRow) {
      return;
    }

    const bodyCells = bodyRow.querySelectorAll('td');
    const headerCells = this.headerTable.nativeElement.querySelectorAll('thead th');

    if (!bodyCells.length || !headerCells.length || bodyCells.length !== headerCells.length) {
      return;
    }

    bodyCells.forEach((td: HTMLTableCellElement, i: number) => {
      const width = td.getBoundingClientRect().width;
      (headerCells[i] as HTMLElement).style.width = `${width}px`;
    });
  }
}
